import i18n from '@/configuration/i18n/i18n.configuration';
import RouteNames from '@/configuration/router/names.routes';
import { ErrorWithToastData } from '@/exceptions/ErrorWithToastData';
import {
  MAX_LENGTH_CONSTRAINT,
  MIN_LENGTH_CONSTRAINT
} from '@/models/doc-review';
import navigationService from '@/services/NavigationService';
import { useToasterStore } from '@/stores/useToasterStore';
import { AxiosError } from 'axios';
import { ErrorCode, ErrorUtils } from './error.utils';
import { OfficeUtils } from './office.utils';
import { formatNumber } from './utils';

/**
 * Handles document length validation errors by showing a toast and redirecting to home
 * if not already on the home route. This provides consistent behavior for both
 * client-side validation failures and server-side DOCUMENT_TOO_LONG errors.
 */
export class GlobalErrorHandler {
  /**
   * Handles DOCUMENT_TOO_LONG errors consistently across the application
   * Shows the same error message as client-side validation and redirects to home
   */
  public static async handleDocumentTooLongError(): Promise<void> {
    const { t } = i18n.global;
    const toaster = useToasterStore();

    try {
      const numberOfWords = await OfficeUtils.getDocumentWordCount();

      const minLength = Number(
        process.env.VUE_APP_REVIEW_REQ_MIN_LENGTH ?? MIN_LENGTH_CONSTRAINT
      );
      const maxLength = Number(
        process.env.VUE_APP_REVIEW_REQ_MAX_LENGTH ?? MAX_LENGTH_CONSTRAINT
      );

      const errorMessage = new ErrorWithToastData(
        t('home.review.validation_error.title'),
        t('home.review.validation_error.body', {
          min: formatNumber(minLength),
          max: formatNumber(maxLength),
          current: formatNumber(numberOfWords)
        })
      );

      toaster.error({
        title: errorMessage.title,
        body: errorMessage.body
      });
    } catch (officeError) {
      console.error('Error while getting document word count:', officeError);
      toaster.error({
        title: t('home.review.validation_error.title'),
        body: t('home.review.validation_error.body', {
          min: formatNumber(MIN_LENGTH_CONSTRAINT),
          max: formatNumber(MAX_LENGTH_CONSTRAINT),
          current: 'unknown'
        })
      });
    }

    const currentRoute = navigationService.getCurrentRoute();
    if (currentRoute?.name !== RouteNames.Home) {
      navigationService.navigateTo({ name: RouteNames.Home });
    }
  }

  /**
   * Checks if an error is a DOCUMENT_TOO_LONG error and handles it appropriately
   * @param error - The error to check and potentially handle
   * @returns true if the error was handled, false otherwise
   */
  public static async handleIfDocumentTooLongError(
    error: unknown
  ): Promise<boolean> {
    const errorCode = this.extractErrorCode(error);

    if (errorCode === ErrorCode.DOCUMENT_TOO_LONG) {
      await this.handleDocumentTooLongError();
      return true;
    }

    return false;
  }

  /**
   * Handles various types of errors including DOCUMENT_TOO_LONG, Axios errors with known error codes,
   * and provides fallback error handling with toast notifications
   * @param error - The error to handle
   * @param toaster - The toaster store instance for showing error messages
   * @param t - The translation function for internationalization
   * @returns true if the error was handled, false otherwise
   */
  public static async handleError(
    error: unknown,
    toaster: ReturnType<typeof useToasterStore>,
    t: (key: string, values?: Record<string, unknown>) => string
  ): Promise<boolean> {
    if (error instanceof ErrorWithToastData) {
      toaster.error({
        title: error.title,
        body: error.body
      });
    }

    let errorToCheck = error;
    if (this.isAxiosErrorWithKnownCode(error)) {
      errorToCheck = this.extractAxiosErrorCode(error as AxiosError);
    }

    const wasDocumentTooLongHandled =
      await this.handleIfDocumentTooLongError(errorToCheck);
    if (wasDocumentTooLongHandled) {
      return true;
    }

    const translatedError = ErrorUtils.getTranslatedError(errorToCheck, t);
    const errorKey = ErrorUtils.toTranslationKey(errorToCheck);
    if (errorKey) {
      toaster.error({
        title: translatedError.title,
        body: translatedError.body
      });

      return false;
    }

    return true;
  }

  /**
   * Convenience method for wrapping try-catch blocks to handle DOCUMENT_TOO_LONG errors
   * @param asyncFn - The async function to execute
   * @param fallbackErrorHandler - Optional fallback error handler for other errors
   */
  public static async wrapWithDocumentValidation<T>(
    asyncFn: () => Promise<T>,
    fallbackErrorHandler?: (error: unknown) => void | Promise<void>
  ): Promise<T | undefined> {
    try {
      return await asyncFn();
    } catch (error) {
      const wasHandled = await this.handleIfDocumentTooLongError(error);

      if (!wasHandled && fallbackErrorHandler) {
        await fallbackErrorHandler(error);
      }

      return undefined;
    }
  }

  /**
   * Extracts error code from various error formats
   */
  private static extractErrorCode(error: unknown): string | null {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      if ('code' in error && typeof error.code === 'string') {
        return error.code;
      }

      if ('message' in error && typeof error.message === 'string') {
        return error.message;
      }

      if ('toString' in error && typeof error.toString === 'function') {
        return error.toString();
      }
    }

    return null;
  }

  /**
   * Checks if the error is an Axios error with a known error code
   */
  private static isAxiosErrorWithKnownCode(error: unknown): boolean {
    if (!(error instanceof AxiosError)) {
      return false;
    }

    const axiosErrorCode = this.extractAxiosErrorCode(error);
    if (!axiosErrorCode) {
      return false;
    }

    return Object.values(ErrorCode).includes(axiosErrorCode as ErrorCode);
  }

  /**
   * Extracts error code from Axios error response
   */
  private static extractAxiosErrorCode(error: AxiosError): string | null {
    if (error.response?.data && Array.isArray(error.response.data)) {
      return error.response.data[0] as string;
    }

    if (error.response?.data && typeof error.response.data === 'string') {
      return error.response.data;
    }

    if (
      error.response?.data &&
      typeof error.response.data === 'object' &&
      'error' in error.response.data
    ) {
      return error.response.data.error as string;
    }

    if (error.response?.status) {
      return error.response.status.toString();
    }

    return null;
  }
}
